import { apiClient } from './client'

// Helper function to build query parameters
function buildQueryParams(params = {}) {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value)
    }
  })
  const query = searchParams.toString()
  return query ? `?${query}` : ''
}

// ===========================
// BRANDS (master_merk)
// ===========================
export const brandService = {
  list(params = {}) {
    const query = buildQueryParams(params)
    return apiClient.get(`/master/brands${query}`)
  },
  create(payload) {
    return apiClient.post('/master/brands', payload)
  },
  get(id) {
    return apiClient.get(`/master/brands/${id}`)
  },
  update(id, payload) {
    return apiClient.put(`/master/brands/${id}`, payload)
  },
  delete(id) {
    return apiClient.delete(`/master/brands/${id}`)
  },
  activate(id) {
    return apiClient.put(`/master/brands/${id}/activate`, {})
  },
  nonActive(id) {
    return apiClient.put(`/master/brands/${id}/non-active`, {})
  }
}

// ===========================
// SUPPLIERS (master_supplier)
// ===========================
export const supplierService = {
  list(params = {}) {
    const query = buildQueryParams(params)
    return apiClient.get(`/master/suppliers${query}`)
  },
  create(payload) {
    return apiClient.post('/master/suppliers', payload)
  },
  get(id) {
    return apiClient.get(`/master/suppliers/${id}`)
  },
  update(id, payload) {
    return apiClient.put(`/master/suppliers/${id}`, payload)
  },
  delete(id) {
    return apiClient.delete(`/master/suppliers/${id}`)
  },
  activate(id) {
    return apiClient.put(`/master/suppliers/${id}/activate`, {})
  },
  nonActive(id) {
    return apiClient.put(`/master/suppliers/${id}/non-active`, {})
  }
}

// ===========================
// CATEGORIES (master_categories)
// ===========================
export const categoryService = {
  list(params = {}) {
    const query = buildQueryParams(params)
    return apiClient.get(`/master/categories${query}`)
  },
  async listAll(params = {}) {
    const allItems = []
    let page = 1
    let total = 0
    do {
      const response = await categoryService.list({ ...params, page, limit: 100 })
      const rows = response?.data || []
      allItems.push(...rows)
      total = Number(response?.meta?.total || response?.total || allItems.length)
      if (rows.length === 0 || allItems.length >= total) break
      page += 1
    } while (page <= 100)
    return allItems
  },
  create(payload) {
    return apiClient.post('/master/categories', payload)
  },
  get(id) {
    return apiClient.get(`/master/categories/${id}`)
  },
  update(id, payload) {
    return apiClient.put(`/master/categories/${id}`, payload)
  },
  delete(id) {
    return apiClient.delete(`/master/categories/${id}`)
  },
  activate(id) {
    return apiClient.put(`/master/categories/${id}/activate`, {})
  },
  nonActive(id) {
    return apiClient.put(`/master/categories/${id}/non-active`, {})
  }
}

// ===========================
// SALES TYPES (jenis penjualan) - from order-types
// ===========================
export const salesTypeService = {
  list(params = {}) {
    const query = buildQueryParams(params)
    return apiClient.get(`/master/order-types${query}`)
  }
}

// ===========================
// UNITS (master_satuan)
// ===========================
export const unitService = {
  list(params = {}) {
    const query = buildQueryParams(params)
    return apiClient.get(`/master/units${query}`)
  },
  async listAll(params = {}) {
    const allItems = []
    let page = 1
    let total = 0
    do {
      const response = await unitService.list({ ...params, page, limit: 100 })
      const rows = response?.data || []
      allItems.push(...rows)
      total = Number(response?.meta?.total || response?.total || allItems.length)
      if (rows.length === 0 || allItems.length >= total) break
      page += 1
    } while (page <= 100)
    return allItems
  },
  create(payload) {
    return apiClient.post('/master/units', payload)
  },
  get(id) {
    return apiClient.get(`/master/units/${id}`)
  },
  update(id, payload) {
    return apiClient.put(`/master/units/${id}`, payload)
  },
  delete(id) {
    return apiClient.delete(`/master/units/${id}`)
  },
  activate(id) {
    return apiClient.put(`/master/units/${id}/activate`, {})
  },
  nonActive(id) {
    return apiClient.put(`/master/units/${id}/non-active`, {})
  }
}

// ===========================
// PRODUCTS (master_products)
// ===========================
export const productService = {
  list(params = {}) {
    const query = buildQueryParams(params)
    return apiClient.get(`/master/products${query}`)
  },
  async listAll(params = {}) {
    const allItems = []
    let page = 1
    let total = 0
    do {
      const response = await productService.list({ ...params, page, limit: 100 })
      const rows = response?.data || response?.items || []
      allItems.push(...rows)
      total = Number(response?.meta?.total || response?.total || allItems.length)
      if (rows.length === 0 || allItems.length >= total) break
      page += 1
    } while (page <= 200)
    return allItems
  },
  create(payload) {
    return apiClient.post('/master/products', payload)
  },
  get(id) {
    return apiClient.get(`/master/products/${id}`)
  },
  update(id, payload) {
    return apiClient.put(`/master/products/${id}`, payload)
  },
  delete(id) {
    return apiClient.delete(`/master/products/${id}`)
  },
  
  // Product Images
  addImage(id, payload) {
    return apiClient.post(`/master/products/${id}/images`, payload)
  },
  deleteImage(id, imageId) {
    return apiClient.delete(`/master/products/${id}/images/${imageId}`)
  },
  setPrimaryImage(id, imageId) {
    return apiClient.put(`/master/products/${id}/images/${imageId}/primary`, {})
  },
  
  // Audit Logs
  getAuditLogs(id, params = {}) {
    const query = buildQueryParams(params)
    return apiClient.get(`/master/products/${id}/audit-logs${query}`)
  },
  
  // Product Variants
  variants: {
    list(productId) {
      return apiClient.get(`/master/products/${productId}/variants`)
    },
    create(productId, payload) {
      return apiClient.post(`/master/products/${productId}/variants`, payload)
    },
    update(productId, variantId, payload) {
      return apiClient.put(`/master/products/${productId}/variants/${variantId}`, payload)
    },
    delete(productId, variantId) {
      return apiClient.delete(`/master/products/${productId}/variants/${variantId}`)
    }
  },

  // Product Selling Prices (Varian Harga Jual / Multi-tier Prices)
  prices: {
    list(productId) {
      return apiClient.get(`/master/products/${productId}/prices`)
    },
    create(productId, payload) {
      return apiClient.post(`/master/products/${productId}/prices`, payload)
    },
    update(productId, priceId, payload) {
      return apiClient.put(`/master/products/${productId}/prices/${priceId}`, payload)
    },
    delete(productId, priceId) {
      return apiClient.delete(`/master/products/${productId}/prices/${priceId}`)
    }
  },
  
  // Product Options (Pilihan Extra)
  // Backend doesn't have specific endpoints for options, they are fully replaced via product update
  options: {
    async create(productId, payload) {
      const { data: product } = await apiClient.get(`/master/products/${productId}`);
      const options = product.options || [];
      options.push(payload);
      return apiClient.put(`/master/products/${productId}`, {
        ...product,
        category_id: product.category_id || undefined,
        unit_id: product.unit_id || undefined,
        options: options,
      });
    },
    async update(productId, optionId, payload) {
      const { data: product } = await apiClient.get(`/master/products/${productId}`);
      const options = (product.options || []).map(opt => 
        opt.id === optionId ? { ...opt, ...payload } : opt
      );
      return apiClient.put(`/master/products/${productId}`, {
        ...product,
        category_id: product.category_id || undefined,
        unit_id: product.unit_id || undefined,
        options: options,
      });
    },
    async delete(productId, optionId) {
      const { data: product } = await apiClient.get(`/master/products/${productId}`);
      const options = (product.options || []).filter(opt => opt.id !== optionId);
      return apiClient.put(`/master/products/${productId}`, {
        ...product,
        category_id: product.category_id || undefined,
        unit_id: product.unit_id || undefined,
        options: options,
      });
    }
  }
}

// ===========================
// SERVICES (master_services)
// ===========================
export const serviceService = {
  list(params = {}) {
    const query = buildQueryParams(params)
    return apiClient.get(`/master/services${query}`)
  },
  async listAll(params = {}) {
    const allItems = []
    let page = 1
    let total = 0
    do {
      const response = await serviceService.list({ ...params, page, limit: 100 })
      const rows = response?.data || response?.items || []
      allItems.push(...rows)
      total = Number(response?.meta?.total || response?.total || allItems.length)
      if (rows.length === 0 || allItems.length >= total) break
      page += 1
    } while (page <= 200)
    return allItems
  },
  create(payload) {
    return apiClient.post('/master/services', payload)
  },
  get(id) {
    return apiClient.get(`/master/services/${id}`)
  },
  update(id, payload) {
    return apiClient.put(`/master/services/${id}`, payload)
  },
  delete(id) {
    return apiClient.delete(`/master/services/${id}`)
  },
  
  // Service Compositions
  compositions: {
    list(serviceId) {
      return apiClient.get(`/master/services/${serviceId}/compositions`)
    },
    create(serviceId, payload) {
      return apiClient.post(`/master/services/${serviceId}/compositions`, payload)
    },
    update(serviceId, compositionId, payload) {
      return apiClient.put(`/master/services/${serviceId}/compositions/${compositionId}`, payload)
    },
    delete(serviceId, compositionId) {
      return apiClient.delete(`/master/services/${serviceId}/compositions/${compositionId}`)
    }
  }
}

// ===========================
// ORDER TYPES (master_order_types)
// ===========================
export const orderTypeService = {
  list(params = {}) {
    const query = buildQueryParams(params)
    return apiClient.get(`/master/order-types${query}`)
  },
  create(payload) {
    return apiClient.post('/master/order-types', payload)
  },
  get(id) {
    return apiClient.get(`/master/order-types/${id}`)
  },
  update(id, payload) {
    return apiClient.put(`/master/order-types/${id}`, payload)
  },
  delete(id) {
    return apiClient.delete(`/master/order-types/${id}`)
  },
  activate(id) {
    return apiClient.put(`/master/order-types/${id}/activate`, {})
  },
  nonActive(id) {
    return apiClient.put(`/master/order-types/${id}/non-active`, {})
  }
}

// ===========================
// ASSET UNITS (master_asset_units)
// ===========================
export const assetUnitService = {
  list(params = {}) {
    const query = buildQueryParams(params)
    return apiClient.get(`/master/asset-units${query}`)
  },
  async listAll(params = {}) {
    let all = []
    let page = 1
    const limit = 100
    while (true) {
      const response = await assetUnitService.list({ ...params, page, limit })
      const items = Array.isArray(response.data) ? response.data : []
      all = all.concat(items)
      const total = response.meta?.total || items.length
      if (all.length >= total || items.length < limit) break
      page++
    }
    return { data: all, meta: { total: all.length } }
  },
  create(payload) {
    return apiClient.post('/master/asset-units', payload)
  },
  get(id) {
    return apiClient.get(`/master/asset-units/${id}`)
  },
  update(id, payload) {
    return apiClient.put(`/master/asset-units/${id}`, payload)
  },
  delete(id) {
    return apiClient.delete(`/master/asset-units/${id}`)
  }
}

// ===========================
// ADDONS (master_addons)
// ===========================
export const addonService = {
  list(params = {}) {
    const query = buildQueryParams(params)
    return apiClient.get(`/master/addons${query}`)
  },
  create(payload) {
    return apiClient.post('/master/addons', payload)
  },
  get(id) {
    return apiClient.get(`/master/addons/${id}`)
  },
  update(id, payload) {
    return apiClient.put(`/master/addons/${id}`, payload)
  },
  delete(id) {
    return apiClient.delete(`/master/addons/${id}`)
  }
}

// ===========================
// ADDON GROUPS (master_addon_groups)
// ===========================
export const addonGroupService = {
  list(params = {}) {
    const query = buildQueryParams(params)
    return apiClient.get(`/master/addon-groups${query}`)
  },
  create(payload) {
    return apiClient.post('/master/addon-groups', payload)
  },
  get(id) {
    return apiClient.get(`/master/addon-groups/${id}`)
  },
  update(id, payload) {
    return apiClient.put(`/master/addon-groups/${id}`, payload)
  },
  delete(id) {
    return apiClient.delete(`/master/addon-groups/${id}`)
  },
  setProductAddonGroups(productId, groupIds) {
    return apiClient.put(`/master/products/${productId}/addon-groups`, { addon_group_ids: groupIds })
  }
}

// ===========================
// TABLES (master_tables)
// ===========================
export const tableService = {
  list(params = {}) {
    const query = buildQueryParams(params)
    return apiClient.get(`/master/tables${query}`)
  },
  create(payload) {
    return apiClient.post('/master/tables', payload)
  },
  get(id) {
    return apiClient.get(`/master/tables/${id}`)
  },
  update(id, payload) {
    return apiClient.put(`/master/tables/${id}`, payload)
  },
  updateStatus(id, payload) {
    return apiClient.put(`/master/tables/${id}/status`, payload)
  },
  delete(id) {
    return apiClient.delete(`/master/tables/${id}`)
  }
}

// ===========================
// STOCK LOCATIONS (stock_locations)
// ===========================
export const stockLocationService = {
  list(params = {}) {
    const query = buildQueryParams(params)
    return apiClient.get(`/master/stock-locations${query}`)
  },
  create(payload) {
    return apiClient.post('/master/stock-locations', payload)
  },
  get(id) {
    return apiClient.get(`/master/stock-locations/${id}`)
  },
  getDetail(id) {
    return apiClient.get(`/master/stock-locations/${id}/detail`)
  },
  update(id, payload) {
    return apiClient.put(`/master/stock-locations/${id}`, payload)
  },
  toggleStatus(id, isActive) {
    return apiClient.patch(`/master/stock-locations/${id}/status`, { is_active: isActive })
  },
  delete(id) {
    return apiClient.delete(`/master/stock-locations/${id}`)
  }
}

// ===========================
// EXPORT ALL SERVICES
// ===========================
export const masterService = {
  brands: brandService,
  suppliers: supplierService,
  categories: categoryService,
  units: unitService,
  products: productService,
  services: serviceService,
  orderTypes: orderTypeService,
  assetUnits: assetUnitService,
  salesTypes: salesTypeService,
  addons: addonService,
  addonGroups: addonGroupService,
  tables: tableService,
  stockLocations: stockLocationService
}
